import { useMemo } from 'react'

import { BidType, MFRR_MARKET_PROGRAMS, Status } from '@/features/bidding/constants'
import type { ExtendedBid } from '@/features/bidding/types/bid'
import type { BidHistory } from '@/features/bidding/types/bidHistory'
import { getLatestExtendedBid } from '@/features/bidding/utils/getLatestBid'
import { findAcceptedBidVersion } from '@/features/bidding/utils/ptuPrefillSources'

type UseBidOfferDerivedDataParams = {
  bidHistories: BidHistory[] | undefined
}

/**
 * Mirrors Portfolio Manager's `isFinlandMfrrEnergyOffer`, which is what decides whether an offer
 * takes the partial flow and therefore whether expired PTUs are validated against the accepted
 * schedule at all. Outside this scope PM still compares against the offered row, so sending accepted
 * volumes there is refused - hence the accepted source is withheld rather than merely unused.
 */
function isFinlandMfrrEnergyBid(bidHistory: BidHistory): boolean {
  return (
    bidHistory.portfolio?.countryCode?.toUpperCase() === 'FI' &&
    MFRR_MARKET_PROGRAMS.includes(bidHistory.marketProgram) &&
    bidHistory.bidType === BidType.ENERGY
  )
}

export function useBidOfferDerivedData({ bidHistories }: UseBidOfferDerivedDataParams) {
  const latestDraftOrOfferedBids = useMemo(() => {
    if (!bidHistories) return undefined

    return bidHistories
      .map((bidHistory) => getLatestExtendedBid(bidHistory.bids))
      .filter((bid) => [Status.DRAFT, Status.OFFERED].includes(bid.status))
  }, [bidHistories])

  /**
   * The ACCEPTED version per activation group - for ENERGY bids this is the version the TSO
   * acknowledged (A01), displayed as ACKNOWLEDGED in the UI.
   *
   * Used to prefill expired PTUs: those rows cannot be edited, so they have to carry the accepted
   * truth rather than the latest offer, which may have been rejected (BID-209). It is derived from
   * the bid histories the page already fetches, so it costs no extra request.
   *
   * This rule ships unconditionally and requires Portfolio Manager's partial flow to be enabled in
   * the environment (`PM_BID_UPDATE_PARTIALS_ENABLED`, `PM_BID_HISTORY_AS_SCHEDULES_ENABLED`,
   * `PM_BID_UPDATES_WRITE_ENABLED` all ON) - with partials on, PM's validation baseline is
   * accepted-first over the same bid table, so this matches on both pre- and post-migration day
   * shapes.
   */
  const latestAcceptedBidsByActivationGroup = useMemo(() => {
    const map = new Map<string, ExtendedBid | null>()

    if (!bidHistories) return map

    for (const bidHistory of bidHistories) {
      if (!isFinlandMfrrEnergyBid(bidHistory)) continue

      map.set(bidHistory.activationGroupUuid, findAcceptedBidVersion(bidHistory.bids ?? []) ?? null)
    }

    return map
  }, [bidHistories])

  const latestNonDraftBidsByActivationGroup = useMemo(() => {
    const map = new Map<string, ExtendedBid | null>()

    if (!bidHistories) return map

    for (const bidHistory of bidHistories) {
      const activationGroupUuid = bidHistory.activationGroupUuid
      const bids = bidHistory.bids ?? []

      // Get all non-draft, non-rejected bids sorted by creation date (newest first). Rejected
      // versions are excluded to mirror Portfolio Manager's
      // `findMostRecentNonDraftNonRejectedBid`, which is the validator's fallback baseline for
      // expired PTUs.
      const nonDraftBids = bids
        .filter((bid) => bid.status !== Status.DRAFT && bid.status !== Status.REJECTED)
        .sort((a, b) => b.createdAt.valueOf() - a.createdAt.valueOf())

      // Get the latest non-draft bid
      const latestNonDraftBid = nonDraftBids.length > 0 ? nonDraftBids[0] : null

      map.set(activationGroupUuid, latestNonDraftBid)
    }

    return map
  }, [bidHistories])

  // Memoize the return object to prevent unnecessary recalculations in dependent hooks
  return useMemo(
    () => ({
      latestDraftOrOfferedBids,
      latestNonDraftBidsByActivationGroup,
      latestAcceptedBidsByActivationGroup,
    }),
    [latestDraftOrOfferedBids, latestNonDraftBidsByActivationGroup, latestAcceptedBidsByActivationGroup],
  )
}
